/**
 * Dynamic delivery information.
 * Calculates delivery messaging and keeps countdown text current.
 */

(function() {
  'use strict';

  var BLOCK_SELECTOR = '[data-delivery-block-id]';
  var intervalId = null;
  var dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var londonDateTimeFormatter = null;

  if (window.Intl && Intl.DateTimeFormat) {
    londonDateTimeFormatter = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });
  }

  /**
   * Returns the current London wall-clock time encoded as UTC. All delivery
   * calculations below use UTC accessors so the UK cutoff remains correct for
   * international visitors and across GMT/BST changes.
   */
  function getLondonWallClock(date) {
    if (londonDateTimeFormatter && londonDateTimeFormatter.formatToParts) {
      var parts = {};

      londonDateTimeFormatter.formatToParts(date).forEach(function(part) {
        if (part.type !== 'literal') {
          parts[part.type] = Number(part.value);
        }
      });

      if (parts.year && parts.month && parts.day && Number.isFinite(parts.hour) && Number.isFinite(parts.minute)) {
        return new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute));
      }
    }

    // Safe legacy fallback: preserve the visitor's wall-clock fields while
    // keeping the calculation API consistent.
    return new Date(Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes()
    ));
  }

  function getMinutesFromMidnight(date) {
    return (date.getUTCHours() * 60) + date.getUTCMinutes();
  }

  function getNextDayName(currentDate, daysToAdd) {
    var futureDate = new Date(currentDate);
    futureDate.setUTCDate(futureDate.getUTCDate() + daysToAdd);
    return dayNames[futureDate.getUTCDay()];
  }

  function formatTimeRemaining(minutes) {
    var safeMinutes = Math.max(0, minutes);

    if (safeMinutes < 60) {
      return safeMinutes + ' minutes';
    }

    var hours = Math.floor(safeMinutes / 60);
    var remainingMinutes = safeMinutes % 60;

    if (remainingMinutes === 0) {
      return hours + ' hour' + (hours > 1 ? 's' : '');
    }

    return hours + ' hour' + (hours > 1 ? 's' : '') + ' ' + remainingMinutes + ' minutes';
  }

  function getDeliveryText(now) {
    var currentDay = now.getUTCDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    var currentTime = getMinutesFromMidnight(now);

    // Monday to Wednesday
    if (currentDay >= 1 && currentDay <= 3) {
      if (currentTime < 15 * 60) {
        var nextDay = getNextDayName(now, 1);
        var minutesToCutoff = (15 * 60) - currentTime;
        return 'Get it ' + nextDay + ' - Order in ' + formatTimeRemaining(minutesToCutoff);
      }

      var dayAfterTomorrow = getNextDayName(now, 2);
      return 'Get it ' + dayAfterTomorrow + ' - Order before 3pm tomorrow';
    }

    // Thursday
    if (currentDay === 4) {
      if (currentTime < 15 * 60) {
        return 'Get it Friday - Order in ' + formatTimeRemaining((15 * 60) - currentTime);
      }

      return 'Get it Saturday - Order before 3pm tomorrow';
    }

    // Friday
    if (currentDay === 5) {
      if (currentTime < 15 * 60) {
        return 'Get it Saturday - Order in ' + formatTimeRemaining((15 * 60) - currentTime);
      }

      // After 3pm Friday, countdown to Saturday 12pm cutoff for Sunday delivery.
      var minutesToSaturdayNoon = ((24 * 60) - currentTime) + (12 * 60);
      return 'Get it Sunday - Order in ' + formatTimeRemaining(minutesToSaturdayNoon);
    }

    // Saturday
    if (currentDay === 6) {
      if (currentTime < 12 * 60) {
        return 'Get it Sunday - Order in ' + formatTimeRemaining((12 * 60) - currentTime);
      }

      return 'Get it Tuesday - Order before Monday at 3pm';
    }

    // Sunday
    return 'Get it Tuesday - Order before Monday at 3pm';
  }

  function parseDeliveryText(deliveryText) {
    var parts = deliveryText.split(' - ');

    if (parts.length === 2) {
      return {
        deliveryDate: parts[0],
        orderDeadline: parts[1]
      };
    }

    return {
      deliveryDate: deliveryText,
      orderDeadline: ''
    };
  }

  function renderDeliveryElement(deliveryElement, deliveryText) {
    if (!deliveryElement) return;

    var deliveryParts = parseDeliveryText(deliveryText);
    var html = '<span class="delivery-information__delivery-date">' + deliveryParts.deliveryDate + '</span>';

    if (deliveryParts.orderDeadline) {
      html += '<span class="delivery-information__separator"> - </span><span class="delivery-information__order-deadline">' + deliveryParts.orderDeadline + '<sup>*</sup></span>';
    }

    deliveryElement.innerHTML = html;
  }

  function renderInternationalDeliveryElement(deliveryElement, countryName) {
    if (!deliveryElement) return;

    var destination = countryName || 'your country';
    var deliveryDate = document.createElement('span');
    var separator = document.createElement('span');
    var checkoutMessage = document.createElement('span');

    deliveryDate.className = 'delivery-information__delivery-date';
    deliveryDate.textContent = 'Delivery to ' + destination;
    separator.className = 'delivery-information__separator';
    separator.textContent = ' — ';
    checkoutMessage.className = 'delivery-information__international-copy';
    checkoutMessage.textContent = 'rates and timing shown at checkout';

    deliveryElement.replaceChildren(deliveryDate, separator, checkoutMessage);
  }

  function updateAllDeliveryBlocks() {
    var londonNow = getLondonWallClock(new Date());
    var deliveryText = getDeliveryText(londonNow);
    var deliveryBlocks = document.querySelectorAll(BLOCK_SELECTOR);

    if (!deliveryBlocks.length) {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
      return;
    }

    deliveryBlocks.forEach(function(block) {
      var blockId = block.getAttribute('data-delivery-block-id');
      if (!blockId) return;

      var deliveryElement = document.getElementById('delivery-day-' + blockId);
      var deliveryMode = block.getAttribute('data-delivery-mode') || 'uk';

      if (deliveryMode === 'international') {
        renderInternationalDeliveryElement(deliveryElement, block.getAttribute('data-delivery-country'));
        return;
      }

      renderDeliveryElement(deliveryElement, deliveryText);
    });
  }

  function startUpdates() {
    updateAllDeliveryBlocks();

    if (!intervalId) {
      intervalId = setInterval(updateAllDeliveryBlocks, 60000);
    }
  }

  function init() {
    if (!document.querySelector(BLOCK_SELECTOR)) return;
    startUpdates();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  // Theme editor section re-render support.
  document.addEventListener('shopify:section:load', function() {
    init();
    updateAllDeliveryBlocks();
  });
})();
